import CaretDown from '@assets/icons/caret-down';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import SubjectDropdown, {
  SubjectDropdownItem,
} from './subject/SubjectDropdown';
import { StudentSubjectMenuList } from '@models/interfaces/parent.interface';
import { useParams } from 'react-router-dom';
import { convertToSlug } from '@utils/helpers/convertToSlug';

type ParentSubjectMenuProps = {
  list: StudentSubjectMenuList[];
};

const ParentSubjectMenu = ({ list }: ParentSubjectMenuProps) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const { student_id, subject_id } = useParams();

  const dropdown_list = useMemo<SubjectDropdownItem[]>(() => {
    return list
      .filter(l => l.is_hybrid)
      .map((l, index) => ({
        key: `${l.student_id}${l.subject_id}`,
        subjectId: parseInt(l.subject_id),
        label: `${l.student_name}, ${l.subject_name}`,
        url: `/parent/${l.student_id}/${convertToSlug(l.subject_name)}`,
        expired: false,
        trial: false,
        order: index,
      }));
  }, [list]);

  const handleToggle = useCallback((show: boolean) => {
    setShowDropdown(show);
  }, []);

  useEffect(() => {
    const handleClickEvent = (e: MouseEvent) => {
      if (!containerRef.current) return;
      const target = e.target as HTMLElement;
      if (!containerRef.current.contains(target)) {
        setShowDropdown(false);
      }
    };

    window.addEventListener('click', handleClickEvent);

    return () => {
      window.removeEventListener('click', handleClickEvent);
    };
  }, []);

  return (
    <div ref={containerRef} className="relative inline-block">
      <div
        className="cursor-pointer"
        onClick={() => handleToggle(!showDropdown)}
      >
        {list
          .filter(
            item =>
              item.student_id === student_id &&
              convertToSlug(item.subject_name) === subject_id,
          )
          .map(student => (
            <div key={`${student.student_id}${student.subject_id}`}>
              <span className="font-black text-lg max-md:!text-md text-primary-navyblue inline-block max-w-[300px] max-lg:max-w-[calc(100vw-56px-86px)] whitespace-nowrap text-ellipsis overflow-hidden">
                {student.student_name}, {student.subject_name}
              </span>
              <CaretDown
                className={
                  'ml-2 w-2.5 items-center inline align-super fill-current text-primary-apricot transition-all ' +
                  (showDropdown ? 'rotate-180' : '')
                }
                data-testid={'caret-down'}
              />
            </div>
          ))}
      </div>
      <div
        className={`absolute top-10 lg:left-0 max-lg:-translate-x-1/2 max-lg:left-1/2 ${
          showDropdown ? '' : 'hidden pointer-events-none'
        }`}
      >
        <SubjectDropdown
          list={dropdown_list}
          onClickItem={() => {
            setShowDropdown(false);
            window.dispatchEvent(new Event('popstate'));
          }}
          closeDropdown={() => {
            setShowDropdown(false);
          }}
        />
      </div>
    </div>
  );
};

export default ParentSubjectMenu;
